import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { DuplicateQueryModal } from '@/sections/insights/queryBuilder/components/Modals';
import { QueryResults } from '@/sections/insights/queryBuilder/components/QueryResults';
import { useQueryOperations } from '@/sections/insights/queryBuilder/hooks/useQueryOperations';
import { useQueryBuilder } from '@/sections/insights/queryBuilder/state/useQueryBuilder';
import type { SavedQuery } from '@/sections/insights/queryBuilder/types/SavedQuery';
import { useAuth } from '@/state/auth';

import { ComingSoonPlaceholder } from './ComingSoonPlaceholder';
import { EmptyState } from './EmptyState';
import { QuerySplitLayout } from './QuerySplitLayout';
import { QuerySimpleVisualizer } from './QuerySplitVisualizer';
import { ActionsToolbar } from './QuerySplitVisualizer/ActionsToolbar';

/**
 * QuerySplitEditor - Editable query interface with split-pane layout
 *
 * Layout:
 * - Left pane: QuerySimpleVisualizer (Praetorian users only) or Coming Soon placeholder
 * - Right pane: EmptyState initially, QueryResults after execution
 * - No action panel (as per design requirements)
 *
 * Behavior:
 * - Matches QueryWriter pattern: EmptyState → QueryResults
 * - Uses QuerySplitLayout for consistent split-pane behavior
 * - Storage key: "querySplitEditorRatio"
 * - Feature gated to Praetorian users during beta
 *
 * Triggered by:
 * - QueryMenu "Edit Query (Split View)" action
 * - QueryBuilderWorkstation when mode === 'split-edit'
 */
export const QuerySplitEditor = () => {
  const { queryState, isGlobal, getSavedQuery } = useQueryBuilder();
  const { isPraetorianUser } = useAuth();
  const [showResults, setShowResults] = useState(false);

  // NEW: Cache the query snapshot from last Execute click
  const [executedQuery, setExecutedQuery] = useState<SavedQuery | null>(null);

  // Add action handlers
  const { handleSave, isSaving } = useQueryOperations();
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);

  const handleDuplicate = () => {
    setIsDuplicateModalOpen(true);
  };

  const handleExecute = () => {
    // Snapshot current query from context
    const currentQuery = getSavedQuery();
    setExecutedQuery(currentQuery);
    setShowResults(true);
  };

  // Reset cache when switching to different query
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Valid use case: resetting UI state when query changes
    setExecutedQuery(null);

    setShowResults(false);
  }, [queryState?.metadata.id]);

  // Error handling for missing query
  if (!queryState) {
    toast.error('No query selected');
    return null;
  }

  return (
    <>
      <QuerySplitLayout
        leftContent={
          <>
            <ActionsToolbar
              editable={true}
              isGlobal={isGlobal}
              onExecute={handleExecute}
              onDuplicate={handleDuplicate}
              onSave={handleSave}
              isSaving={isSaving}
            />
            <div className="relative h-full">
              {isPraetorianUser ? (
                <QuerySimpleVisualizer editable={true} />
              ) : (
                <ComingSoonPlaceholder />
              )}
            </div>
          </>
        }
        rightContent={
          showResults ? (
            <QueryResults selectedQuery={executedQuery} />
          ) : (
            <EmptyState />
          )
        }
        storageKey="querySplitEditorRatio"
        testId="query-split-editor"
      />

      {/* Duplicate Modal */}
      {getSavedQuery() && (
        <DuplicateQueryModal
          isOpen={isDuplicateModalOpen}
          onClose={() => setIsDuplicateModalOpen(false)}
          originalQuery={getSavedQuery()!}
        />
      )}
    </>
  );
};
